import { useCallback, useRef, useState } from 'react'
import { Button, Checkbox, Group, NumberInput, Stack, Table, Text, TextInput, Textarea } from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal } from '@/components/ui'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { downloadBlob } from '@/lib/download-blob'
import type { AIVariableEntry } from '@/types/api'

type Props = {
  projectId: string
  variables: AIVariableEntry[]
  canEdit: boolean
  onUpdated: () => void
}

function normalizeVariableKey(raw: string): string {
  return raw
    .trim()
    .replace(/^\[+|\]+$/g, '')
    .replace(/\s+/g, '_')
}

export function AiVariablesTab({ projectId, variables, canEdit, onUpdated }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [modal, setModal] = useState<{ index: number | null; entry: AIVariableEntry } | null>(null)
  const [saving, setSaving] = useState(false)
  const [key, setKey] = useState('')
  const [description, setDescription] = useState('')
  const [collecting, setCollecting] = useState(false)
  const [required, setRequired] = useState(false)
  const [askLabel, setAskLabel] = useState('')
  const [priority, setPriority] = useState<number | ''>('')

  const openAdd = () => {
    setModal({ index: null, entry: { key: '', description: '' } })
    setKey('')
    setDescription('')
    setRequired(false)
    setAskLabel('')
    setPriority('')
  }

  const openEdit = (index: number, entry: AIVariableEntry) => {
    setModal({ index, entry })
    setKey(entry.key || '')
    setDescription(entry.description || '')
    setRequired(Boolean(entry.required))
    setAskLabel(entry.ask_label || '')
    setPriority(typeof entry.priority === 'number' ? entry.priority : '')
  }

  const persistVariables = useCallback(
    async (next: AIVariableEntry[]) => {
      await api.projectAIUpdate(projectId, { variables: next })
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      onUpdated()
    },
    [projectId, onUpdated],
  )

  const saveVar = async () => {
    const k = normalizeVariableKey(key)
    if (!k) {
      showNotification({ title: 'Форма', message: 'Ключ обязателен', color: 'yellow' })
      return
    }
    const desc = description.trim()
    const label = askLabel.trim()
    const next = [...variables]
    const row: AIVariableEntry = {
      key: k,
      description: desc || undefined,
      required,
      ask_label: label || undefined,
      priority: typeof priority === 'number' ? priority : undefined,
    }
    if (modal?.index != null && modal.index >= 0) {
      next[modal.index] = row
    } else {
      next.push(row)
    }
    const nextDeduped = next.filter(
      (item, index, arr) =>
        arr.findIndex((x) => String(x.key || '').toLowerCase() === String(item.key || '').toLowerCase()) === index,
    )
    setSaving(true)
    try {
      await persistVariables(nextDeduped)
      setModal(null)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const deleteAt = (index: number) => {
    const next = variables.filter((_, i) => i !== index)
    void persistVariables(next).catch((e) =>
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      }),
    )
  }

  const exportExcel = async () => {
    try {
      const blob = await api.exportAIVariablesExcel(projectId)
      downloadBlob(blob, `variables-${projectId}.xlsx`)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      })
    }
  }

  const runCollect = async () => {
    setCollecting(true)
    try {
      const res = await api.collectVariablesFromInstructions(projectId)
      showNotification({
        title: 'Сбор переменных',
        message: res.message || `Собрано ${res.count ?? 0} переменных`,
        color: 'green',
      })
      onUpdated()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      })
    } finally {
      setCollecting(false)
    }
  }

  const collect = () =>
    openConfirmModal({
      title: 'Собрать переменные из инструкций?',
      children: (
        <Text size="sm">
          Будут добавлены значения из поля «какие данные собирает». Существующие описания сохранятся.
        </Text>
      ),
      labels: { confirm: 'Собрать', cancel: 'Отмена' },
      onConfirm: () => void runCollect(),
    })

  return (
    <Stack gap="md">
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (!f || !canEdit) return
          void api
            .uploadAIVariablesExcel(projectId, f)
            .then((res) => {
              const msg =
                typeof res === 'object' && res && 'message' in res
                  ? String((res as { message?: string }).message)
                  : 'Готово'
              showNotification({ title: 'Импорт', message: msg, color: 'green' })
              onUpdated()
            })
            .catch((err) =>
              showNotification({
                title: 'Ошибка',
                message: err instanceof ApiError ? err.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                color: 'red',
              }),
            )
            .finally(() => {
              if (fileRef.current) fileRef.current.value = ''
            })
        }}
      />
      <Group gap="xs" wrap="wrap">
        {canEdit ? (
          <Button size="sm" onClick={openAdd}>
            Добавить переменную
          </Button>
        ) : null}
        <Button size="sm" variant="default" onClick={() => void exportExcel()}>
          Выгрузить Excel
        </Button>
        {canEdit ? (
          <Button size="sm" variant="light" onClick={() => fileRef.current?.click()}>
            Загрузить Excel
          </Button>
        ) : null}
        {canEdit ? (
          <Button size="sm" variant="light" loading={collecting} onClick={() => collect()}>
            Собрать из инструкций
          </Button>
        ) : null}
      </Group>

      {variables.length === 0 ? (
        <Text size="sm" c="dimmed">
          Переменных нет
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Ключ</Table.Th>
              <Table.Th>Описание</Table.Th>
              <Table.Th>Обяз.</Table.Th>
              <Table.Th>Приоритет</Table.Th>
              <Table.Th w={180} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {variables.map((v, index) => (
              <Table.Tr key={`${v.key}-${index}`}>
                <Table.Td style={{ fontFamily: 'monospace' }}>{v.key}</Table.Td>
                <Table.Td>{v.description || '—'}</Table.Td>
                <Table.Td>{v.required ? 'Да' : 'Нет'}</Table.Td>
                <Table.Td>{typeof v.priority === 'number' ? v.priority : '—'}</Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    <Button size="compact-xs" variant="default" onClick={() => openEdit(index, v)}>
                      Изменить
                    </Button>
                    {canEdit ? (
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="light"
                        onClick={() =>
                          openConfirmModal({
                            title: 'Удалить переменную?',
                            onConfirm: () => deleteAt(index),
                          })
                        }
                      >
                        Удалить
                      </Button>
                    ) : null}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <AppModal
        opened={modal != null}
        onClose={() => setModal(null)}
        title="Переменная"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setModal(null)} />
            <ActionButton action="save" loading={saving} onClick={() => void saveVar()} />
          </>
        }
      >
        <Stack gap="sm">
          <TextInput label="Ключ" required value={key} onChange={(e) => setKey(e.target.value)} />
          <Textarea label="Описание" value={description} onChange={(e) => setDescription(e.target.value)} minRows={3} />
          <Checkbox
            label="Обязательное поле для запроса данных"
            checked={required}
            onChange={(e) => setRequired(e.currentTarget.checked)}
          />
          <TextInput
            label="Текст вопроса (опционально)"
            placeholder="Например: Email подписки"
            value={askLabel}
            onChange={(e) => setAskLabel(e.target.value)}
          />
          <NumberInput
            label="Приоритет (меньше = раньше)"
            min={0}
            max={9999}
            value={priority}
            onChange={(v) => setPriority(typeof v === 'number' ? v : '')}
          />
        </Stack>
      </AppModal>
    </Stack>
  )
}
